import { Directive, EventEmitter, Input, Output, ViewContainerRef } from '@angular/core';
import { QualificationOutcome } from 'src/app/core/enums/lead/qualification-outcome.enum';
import { QualificationStatus } from 'src/app/core/enums/lead/qualification-status.enum';
import { AuditCategory, LeadAudit } from 'src/app/core/models/lead/lead-audit.model';
import { AuditItemAssignmentComponent } from './audit-item-assignment/audit-item-assignment.component';
import { AuditItemDataChangeComponent } from './audit-item-data-change/audit-item-data-change.component';
import { AuditItemInjectionComponent } from './audit-item-injection/audit-item-injection.component';
import { AuditItemLogComponent } from './audit-item-log/audit-item-log.component';
import { AuditItemOfferComponent } from './audit-item-offer/audit-item-offer.component';
import { AuditItemProcessingDoneComponent } from './audit-item-processing-done/audit-item-processing-done.component';
import { AuditItemRefusalComponent } from './audit-item-refusal/audit-item-refusal.component';
import { AuditItemAutoOfferFailComponent } from './audit-item-auto-offer-fail/audit-item-auto-offer-fail.component';
import { AuditItemCreationComponent } from './audit-item-creation/audit-item-creation.component';

@Directive({
    selector: '[appAuditItem]',
    standalone: false
})
export class AuditItemDirective {

  @Input() set audit(audit: LeadAudit) {
    this.viewContainerRef.clear()
    if (!audit) return

    this.setContent(audit)
  }

  @Output() selected: EventEmitter<LeadAudit> = new EventEmitter<LeadAudit>()
  @Output() unselected: EventEmitter<LeadAudit> = new EventEmitter<LeadAudit>()

  constructor(private viewContainerRef: ViewContainerRef) {
  }

  private setContent(audit: LeadAudit) {
    let creationStrategy;

    switch (audit.category) {
      case AuditCategory.Injection: creationStrategy = () => this.setItemInjection(audit); break;
      case AuditCategory.Creation: creationStrategy = () => this.setItemCreation(audit); break;
      case AuditCategory.Assignment: creationStrategy = () => this.setItemAssignment(audit); break;
      case AuditCategory.Log: creationStrategy = () => this.setItemLog(audit); break;
      case AuditCategory.Offer: creationStrategy = () => this.setItemOffer(audit); break;
      case AuditCategory.ProcessingDone: creationStrategy = () => this.setItemProcessingDone(audit); break;
      case AuditCategory.Refusal: creationStrategy = () => this.setItemRefusal(audit); break;
      case AuditCategory.DataChange: creationStrategy = () => this.setItemDataChange(audit); break;
      case AuditCategory.AutoOfferFail: creationStrategy = () => this.setItemAutoOfferFail(audit); break;

      default:
        creationStrategy = () => { }
        break;
    }

    creationStrategy()
  }

  setItemInjection(audit: LeadAudit) {
    let componentRef = this.viewContainerRef.createComponent(AuditItemInjectionComponent)

    const data = audit.data as Object

    componentRef.instance.input = {
      updated: audit.updated,
      comment: audit.comment,
      operator: audit.operator,
      finalOwner: audit.finalOwner,
      initialOwner: audit.initialOwner
    }

    componentRef.instance.selected.subscribe(() => this.selected.emit(audit))
    componentRef.instance.unselected.subscribe(() => this.unselected.emit())
  }

  setItemCreation(audit: LeadAudit) {
    let componentRef = this.viewContainerRef.createComponent(AuditItemCreationComponent)

    const data = audit.data as Object

    componentRef.instance.input = {
      updated: audit.updated,
      comment: audit.comment,
      operator: audit.operator,
      finalOwner: audit.finalOwner,
      initialOwner: audit.initialOwner
    }

    componentRef.instance.selected.subscribe(() => this.selected.emit(audit))
    componentRef.instance.unselected.subscribe(() => this.unselected.emit())
  }

  setItemRefusal(audit: LeadAudit) {
    let componentRef = this.viewContainerRef.createComponent(AuditItemRefusalComponent)

    const data = audit.data as Object

    componentRef.instance.input = {
      updated: audit.updated,
      comment: audit.comment,
      operator: audit.operator,
      finalOwner: audit.finalOwner,
      initialOwner: audit.initialOwner
    }

    componentRef.instance.selected.subscribe(() => this.selected.emit(audit))
    componentRef.instance.unselected.subscribe(() => this.unselected.emit())
  }

  setItemProcessingDone(audit: LeadAudit) {
    let componentRef = this.viewContainerRef.createComponent(AuditItemProcessingDoneComponent)

    const data = audit.data as any

    componentRef.instance.input = {
      updated: audit.updated,
      comment: audit.comment,
      operator: audit.operator,
      finalOwner: audit.finalOwner,
      initialOwner: audit.initialOwner,
      qualificationStatus: QualificationStatus[data.status as keyof typeof QualificationStatus],
      qualificationOutcome: QualificationOutcome[data.outcome as keyof typeof QualificationOutcome],
    }

    componentRef.instance.selected.subscribe(() => this.selected.emit(audit))
    componentRef.instance.unselected.subscribe(() => this.unselected.emit())
  }

  setItemOffer(audit: LeadAudit) {
    let componentRef = this.viewContainerRef.createComponent(AuditItemOfferComponent)

    const data = audit.data as any

    componentRef.instance.input = {
      updated: audit.updated,
      comment: audit.comment,
      operator: audit.operator,
      finalOwner: audit.finalOwner,
      initialOwner: audit.initialOwner,
      offeree: data['offeree'] as number,
      expiration: new Date(data['expiration'])
    }

    componentRef.instance.selected.subscribe(() => this.selected.emit(audit))
    componentRef.instance.unselected.subscribe(() => this.unselected.emit())
  }

  setItemLog(audit: LeadAudit) {
    let componentRef = this.viewContainerRef.createComponent(AuditItemLogComponent)

    const data = audit.data as any

    componentRef.instance.input = {
      updated: audit.updated,
      comment: audit.comment,
      operator: audit.operator,
      finalOwner: audit.finalOwner,
      initialOwner: audit.initialOwner,
      severity: data['severity'],
      source: data['source'],
      message: data['message'],
    }

    componentRef.instance.selected.subscribe(() => this.selected.emit(audit))
    componentRef.instance.unselected.subscribe(() => this.unselected.emit())
  }

  setItemAssignment(audit: LeadAudit) {
    let componentRef = this.viewContainerRef.createComponent(AuditItemAssignmentComponent)

    const data = audit.data as any

    componentRef.instance.input = {
      updated: audit.updated,
      comment: audit.comment,
      operator: audit.operator,
      finalOwner: audit.finalOwner,
      initialOwner: audit.initialOwner
    }

    componentRef.instance.selected.subscribe(() => this.selected.emit(audit))
    componentRef.instance.unselected.subscribe(() => this.unselected.emit())
  }

  setItemDataChange(audit: LeadAudit) {
    let componentRef = this.viewContainerRef.createComponent(AuditItemDataChangeComponent)

    const data = audit.data as any

    componentRef.instance.input = {
      updated: audit.updated,
      comment: audit.comment,
      operator: audit.operator,
      finalOwner: audit.finalOwner,
      initialOwner: audit.initialOwner,
      entity: data['entity'],
      operation: data['operation'],
      oldData: data['oldData'],
      newData: data['newData']
    }

    componentRef.instance.selected.subscribe(() => this.selected.emit(audit))
    componentRef.instance.unselected.subscribe(() => this.unselected.emit())
  }

  setItemAutoOfferFail(audit: LeadAudit) {
    let componentRef = this.viewContainerRef.createComponent(AuditItemAutoOfferFailComponent)

    const data = audit.data as any

    componentRef.instance.input = {
      updated: audit.updated,
      comment: audit.comment,
      operator: audit.operator,
      finalOwner: audit.finalOwner,
      initialOwner: audit.initialOwner
    }

    componentRef.instance.selected.subscribe(() => this.selected.emit(audit))
    componentRef.instance.unselected.subscribe(() => this.unselected.emit())
  }

}
